"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useSession } from "next-auth/react";
import { Loader2, Send, CheckCircle2, AlertCircle, LogIn } from "lucide-react";
import { api } from "@/trpc/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getSupportTone, type SupportTone } from "../_components/supportTheme";

interface SupportTicketFormProps {
  onSuccess?: () => void;
  tone?: SupportTone;
  isDark?: boolean;
}

export function SupportTicketForm({
  onSuccess,
  tone,
  isDark = false,
}: SupportTicketFormProps) {
  const t = tone ?? getSupportTone(isDark);
  const { data: session, status } = useSession();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const createTicket = api.support.createTicket.useMutation();
  const isAuthenticated = status === "authenticated";
  const isAuthLoading = status === "loading";
  const loginHref = useMemo(() => "/login?from=/support", []);

  useEffect(() => {
    if (status === "authenticated" && session?.user?.email) {
      setEmail(session.user.email);
      return;
    }
    if (status === "unauthenticated") {
      setEmail("");
    }
  }, [status, session?.user?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isAuthenticated) {
      setErrorMessage("Please sign in to submit a support ticket");
      setSubmitStatus("error");
      return;
    }

    if (!email.trim() || !title.trim() || !description.trim()) {
      setErrorMessage("Please fill in all required fields");
      setSubmitStatus("error");
      return;
    }

    setSubmitStatus("idle");
    setErrorMessage("");

    try {
      await createTicket.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        contact_email: email.trim(),
      });

      setSubmitStatus("success");
      setTitle("");
      setDescription("");

      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting ticket:", error);
      setSubmitStatus("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to submit ticket. Please try again.",
      );
    }
  };

  const isSubmitting = createTicket.isPending;

  const inputClass = cn(
    "h-10 rounded-[10px] border px-3.5 text-[13.5px] transition-all focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60",
    t.input,
  );
  const labelClass = cn(
    "font-ui mb-1.5 block text-[9.5px] font-medium tracking-[0.16em] uppercase",
    t.label,
  );
  const authGateClass = "border-current/15 bg-current/[0.035] text-current";
  const authGateMutedClass = "text-current opacity-70";
  const authGateButtonClass =
    "border-current/15 bg-transparent text-current hover:border-current/25 hover:bg-current/[0.045] hover:text-current";

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[16px] border shadow-[var(--zom-public-panel-shadow-strong)]",
        t.panelBorder,
        t.panel,
      )}
    >
      <div className={cn("border-b px-5 py-4 sm:px-6", t.divider)}>
        <h2
          className={cn(
            "font-display text-[1.7rem] leading-[1.05] font-light",
            t.heading,
          )}
        >
          Submit a ticket
        </h2>
        <p className={cn("font-body mt-1.5 text-[13px] leading-5", t.body)}>
          Tell us what&apos;s going on and we&apos;ll respond in-app and over
          email when there&apos;s an update.
        </p>
      </div>

      <div className="px-5 py-5 sm:px-6">
        {isAuthLoading ? (
          <div
            className={cn(
              "flex items-center gap-3 rounded-[12px] border p-4",
              authGateClass,
            )}
          >
            <Loader2 className="h-4 w-4 animate-spin opacity-75" />
            <p className={cn("text-[13px] font-medium", authGateMutedClass)}>
              Checking your sign-in status...
            </p>
          </div>
        ) : !isAuthenticated ? (
          <div
            className={cn(
              "flex flex-col gap-4 rounded-[12px] border p-4",
              authGateClass,
            )}
          >
            <div>
              <p className="font-display text-[14px] font-medium text-current">
                Sign in to send a support ticket
              </p>
              <p
                className={cn(
                  "font-body mt-1.5 text-[13px] leading-5",
                  authGateMutedClass,
                )}
              >
                We&apos;ll bring you back here so your email, title, and
                description can be submitted securely.
              </p>
            </div>
            <Button
              asChild
              variant="outline"
              className={cn(
                "font-body h-10 w-fit rounded-[10px] px-4 text-[13px] font-medium",
                authGateButtonClass,
              )}
            >
              <Link href={loginHref}>
                <LogIn className="h-4 w-4" />
                Sign in
              </Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="support-email"
                className={`${labelClass} font-mono`}
              >
                Email <span className={t.accent}>*</span>
              </label>
              <Input
                id="support-email"
                type="email"
                autoComplete="email"
                placeholder="you@advisors.com"
                value={email}
                readOnly
                className={`${inputClass} font-body`}
                disabled={!isAuthenticated || isSubmitting}
                required
              />
            </div>

            <div>
              <label
                htmlFor="support-title"
                className={`${labelClass} font-mono`}
              >
                Title <span className={t.accent}>*</span>
              </label>
              <Input
                id="support-title"
                type="text"
                placeholder="Brief description of your issue"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`${inputClass} font-body`}
                disabled={!isAuthenticated || isSubmitting}
                required
              />
            </div>

            <div>
              <label
                htmlFor="support-description"
                className={`${labelClass} font-mono`}
              >
                Description <span className={t.accent}>*</span>
              </label>
              <Textarea
                id="support-description"
                placeholder="Please provide detailed information about your issue or question..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={cn(
                  "font-body min-h-[112px] resize-none py-3",
                  inputClass,
                )}
                disabled={!isAuthenticated || isSubmitting}
                required
              />
            </div>

            {submitStatus === "success" && (
              <div
                className={cn(
                  "flex items-start gap-3 rounded-[12px] border p-3.5",
                  t.success,
                )}
              >
                <CheckCircle2
                  className={cn("mt-0.5 h-5 w-5 shrink-0", t.successIcon)}
                />
                <div className="flex-1 space-y-1">
                  <p
                    className={cn(
                      "font-display text-[14px] font-medium",
                      t.successTitle,
                    )}
                  >
                    Ticket submitted successfully!
                  </p>
                  <p
                    className={cn(
                      "font-body text-[13px] leading-5",
                      t.successBody,
                    )}
                  >
                    You&apos;ll receive a confirmation email shortly. Our team
                    will review your request and follow up.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === "error" && errorMessage && (
              <div
                className={cn(
                  "flex items-start gap-3 rounded-[12px] border p-3.5",
                  t.error,
                )}
              >
                <AlertCircle
                  className={cn("mt-0.5 h-5 w-5 shrink-0", t.errorIcon)}
                />
                <div className="flex-1 space-y-1">
                  <p
                    className={cn(
                      "font-display text-[14px] font-medium",
                      t.errorTitle,
                    )}
                  >
                    Submission failed
                  </p>
                  <p
                    className={cn(
                      "font-body text-[13.5px] leading-6",
                      t.errorBody,
                    )}
                  >
                    {errorMessage}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end">
              <Button
                type="submit"
                disabled={
                  !isAuthenticated || isSubmitting || submitStatus === "success"
                }
                className={cn(
                  "font-body h-10 rounded-[10px] px-5 text-[13px] font-medium transition-all duration-300 hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100",
                  t.btnPrimary,
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Submit ticket
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
